import { useCallback } from 'react';
import type { FlatConfig } from 'domain/index';
import type { StorageVariant } from './storageSetSummary';
import type { Mapping } from './storageSetOptions';

export type StorageSetHandlersInput = {
  variant: StorageVariant;
  config: FlatConfig;
  updateConfig: (patch: Partial<FlatConfig>) => void;
  ssdOptions: Mapping[];
  hddOptions: Mapping[];
  minNodes: number;
  enabled: boolean;
  e2e: boolean;
};

/** Every `on*` handler the storage-set section needs, split out of
 *  useStorageSetSection per R14 so the composing hook stays under the
 *  max-lines-per-function / complexity budgets. Semantics unchanged from the
 *  original: each handler is exactly one updateConfig call using the classic
 *  flat-config keys (§1.6 rule 1); disabling a set enables the other so a
 *  system never has zero. */
export function useStorageSetHandlers({
  variant, config, updateConfig, ssdOptions, hddOptions, minNodes, enabled, e2e,
}: StorageSetHandlersInput) {
  const flash = variant === 'flash';

  const onToggleEnabled = useCallback(() => {
    const next = !enabled;
    // One side must stay enabled; disabling this one turns the other on.
    updateConfig(flash
      ? { afaEnabled: next, hybridEnabled: next ? config.hybridEnabled : true }
      : { hybridEnabled: next, afaEnabled: next ? config.afaEnabled : true });
  }, [enabled, flash, config.afaEnabled, config.hybridEnabled, updateConfig]);

  // CONF-217: both take the NUMBER useNumberDraft commits on blur/Enter (the
  // view no longer commits per keystroke); the floor is re-applied here as a
  // last line of defence, as before.
  const onNodeCountChange = useCallback((n: number) => {
    const count = Math.max(minNodes, Number.isFinite(n) ? Math.trunc(n) : minNodes);
    updateConfig(flash ? { afaNodeCount: count } : { hybridNodeCount: count });
  }, [flash, minNodes, updateConfig]);

  const onMinRawPbChange = useCallback((pb: number) => {
    const tb = Number.isFinite(pb) && pb > 0 ? pb * 1000 : 0;
    updateConfig(flash ? { afaMinRawCapacity: tb } : { hybridMinRawCapacity: tb });
  }, [flash, updateConfig]);

  const onSsdChange = useCallback((partNumber: string) => {
    const m = ssdOptions.find((o) => o.partNumber === partNumber);
    if (!m) return;
    updateConfig(flash
      ? { afaSsdPartNumber: partNumber, afaVpodSsdCapacity: m.capacity }
      : { hybridSsdPartNumber: partNumber, hybridVpodSsdCapacity: m.capacity });
  }, [flash, ssdOptions, updateConfig]);

  const onJbodChange = useCallback((v: string) => updateConfig({ jbodSize: Number(v) }), [updateConfig]);

  const onHddChange = useCallback((partNumber: string) => {
    const m = hddOptions.find((o) => o.partNumber === partNumber);
    if (m) updateConfig({ hybridHddCapacity: m.capacity, hybridHddPartNumber: partNumber });
  }, [hddOptions, updateConfig]);

  const onEncodingChange = useCallback((v: string) => {
    updateConfig(flash
      ? { afaEncodingScheme: v, afaEncodingSchemeUserSelected: true }
      : { hybridEncodingScheme: v, hybridEncodingSchemeUserSelected: true });
  }, [flash, updateConfig]);

  const onSparesChange = useCallback((v: string) => {
    updateConfig(flash ? { afaSpares: parseInt(v, 10) } : { hybridSpares: parseInt(v, 10) });
  }, [flash, updateConfig]);

  const onCompressionChange = useCallback((v: string) => {
    updateConfig(flash ? { afaCompressionRatio: parseFloat(v) } : { hybridCompressionRatio: parseFloat(v) });
  }, [flash, updateConfig]);

  const onE2eToggle = useCallback(() => {
    updateConfig(flash ? { afaEndToEndEncryption: !e2e } : { hybridEndToEndEncryption: !e2e });
  }, [flash, e2e, updateConfig]);

  // CONF-94: the Switch hands over the next state; R12 still clears it if the
  // vendor or JBOD can't support MLEC.
  const onMlecChange = useCallback((next: boolean) => updateConfig({ hybridMLEC: next }), [updateConfig]);

  // CONF-210: written explicitly (never defaulted) so a fresh config carries no
  // key and the frozen snapshots stay put; see domain/flatConfig.ts.
  const onNodeModelChange = useCallback((v: string) => {
    updateConfig({ afaNodeModel: v === 'dualCpu' ? 'dualCpu' : 'standard' });
  }, [updateConfig]);

  return {
    onToggleEnabled, onNodeCountChange, onMinRawPbChange,
    onSsdChange, onJbodChange, onHddChange, onEncodingChange,
    onSparesChange, onCompressionChange, onE2eToggle, onNodeModelChange, onMlecChange,
  };
}
